import Cookies from "js-cookie";
import { lazy, useEffect } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import MainLayout from "./components/layouts/MainLayout";
import useAuth from "./hooks/useAuth";
import IndexPage from "./pages";
import HomePage from "./pages/homepage";
import { SideBarRouteOption } from "./types";
import { ROLE_TO_ROUTES } from "./utils/constants";

import SuspenseWithLoader from "./components/common/SuspenseWithLoader";
import LicencingAdminRoutes from "./utils/routes/licencingAdminRoutes";
import licencingApprovalOfficesRoutes from "./utils/routes/licencingApprovalOfficesRoutes";
import licencingEndUserRoutes from "./utils/routes/licencingEndUserRoutes";

const AcceptInvitation = lazy(
  () => import("@/pages/auth/AcceptInvitation.tsx")
);

const AnalyticsDashboard = lazy(() => import("@/pages/analytics/index"));

const ApprovalOfficerAnalyticsDashboard = lazy(
  () => import("@/pages/approval-officer-analytics/index")
);

const LicencingAdminPostSecondaryEducation = lazy(
  () =>
    import(
      "@/pages/licencing-admin/post-secondary-education/professionscategory"
    )
);
const LicencingAdminPostSecondaryEducationExitAward = lazy(
  () => import("@/pages/licencing-admin/post-secondary-education/exitaward")
);
const LicencingAdminSecondaryEducationDocumentTitle = lazy(
  () => import("@/pages/licencing-admin/secondary-education/documenttitle")
);
const LicencingAdminSecondaryEducationProfessionsCredential = lazy(
  () =>
    import(
      "@/pages/licencing-admin/post-secondary-education/professionscredential"
    )
);
const LicencingAdminControlPanelBankConfiguration = lazy(
  () => import("@/pages/licencing-admin/control-panel/bankconfiguration")
);
const LicencingAdminControlPanelLicenceFeeConfiguration = lazy(
  () => import("@/pages/licencing-admin/control-panel/licencefeeconfiguration")
);
const LicencingAdminServicesConfiguration = lazy(
  () => import("@/pages/licencing-admin/control-panel/services-configuration")
);
const ApprovalStructure = lazy(
  () => import("@/pages/licencing-admin/system-settings/ApprovalStructure")
);
const LicencingAdminUniversityConfiguration = lazy(
  () =>
    import(
      "@/pages/licencing-admin/post-secondary-education/universityconfiguration"
    )
);
const LicencingAdminRoles = lazy(
  () => import("./pages/licencing-admin/system-settings/LicencingAdminRoles")
);
const AcademicDegreeProgram = lazy(
  () =>
    import(
      "@/pages/licencing-admin/post-secondary-education/academicdegreeprogram"
    )
);
const LicencingAdminSystemUsers = lazy(
  () =>
    import("./pages/licencing-admin/system-settings/LicencingAdminSystemUsers")
);
const LicencingEndUserAnalytics = lazy(
  () => import("./pages/licencing-end-user/statistics")
);
const LicencingApprovalOfficesAccountStatistics = lazy(
  () => import("./pages/approval-offices-account/statistics")
);

const LicencingApprovalOfficesAccountIndexingRejected = lazy(
  () => import("./pages/approval-offices-account/rejectedreports/indexing")
);

const LicencingApprovalOfficesAccountAllApplications = lazy(
  () =>
    import(
      "./pages/approval-offices-account/application/LicencingApprovalOfficesAccountAllApplications"
    )
);

const LicencingApprovalOfficesAccountApplicationsModifications = lazy(
  () =>
    import(
      "./pages/approval-offices-account/application/LicencingApprovalOfficesAccountApplicationsModifications"
    )
);

const LicencingApprovalOfficesAccountManualAssignRegNo = lazy(
  () =>
    import(
      "./pages/approval-offices-account/modifications/LicencingApprovalOfficesAccountManualAssignRegNo"
    )
);

const LicencingApprovalOfficesAccountManualAssignIndexing = lazy(
  () =>
    import(
      "./pages/approval-offices-account/modifications/LicencingApprovalOfficesAccountManualAssignIndexing"
    )
);
const LicencingApprovalOfficesAccountManualAssignExistingIndex = lazy(
  () =>
    import(
      "./pages/approval-offices-account/modifications/LicencingApprovalOfficesAccountManualAssignExistingIndex"
    )
);

const LicencingApprovalOfficesAccountManualAssignExistingRegistration = lazy(
  () =>
    import(
      "./pages/approval-offices-account/modifications/LicencingApprovalOfficesAccountManualAssignExistingRegistration"
    )
);

const LicencingApprovalOfficesAccountOnHoldApplications = lazy(
  () =>
    import(
      "./pages/approval-offices-account/application/LicencingApprovalOfficesAccountOnHoldApplications"
    )
);

const LicencingApprovalOfficesAccountApprovedApplications = lazy(
  () =>
    import(
      "./pages/approval-offices-account/application/LicencingApprovalOfficesAccountApprovedApplications"
    )
);

const LicencingApprovalOfficesAccountLecenseRenew = lazy(
  () => import("./pages/approval-offices-account/rejectedreports/licenserenew")
);

const RegistrationFeeApplications = lazy(
  () => import("@/pages/approval-offices-account/ServicesApplications")
);

const ProfileView = lazy(() => import("./pages/profile/Index"));
const UserProfileSettings = lazy(
  () => import("./pages/userprofilesettings/Index")
);

const UserCertificates = lazy(() => import("./pages/usercertificates/Index"));

const UserCertificatesDownload = lazy(
  () => import("./pages/usercertificatesdownload/Index")
);

const UserProfileRecords = lazy(
  () => import("./pages/userprofilerecords/Index")
);
const VerifyEmail = lazy(() => import("@/pages/auth/VerifyEmail.tsx"));
const LoginPage = lazy(() => import("@/pages/auth/login"));
const ResetPassword = lazy(
  () => import("@/pages/auth/reset-password/ResetPassword.tsx")
);

const ResetPasswordByLicense = lazy(
  () =>
    import("@/pages/auth/reset-password-bylicense/ResetPasswordByLicense.tsx")
);
const VerifyResetCode = lazy(
  () => import("@/pages/auth/reset-password/VerifyResetCode.tsx")
);
const CreateAccount = lazy(
  () => import("@/pages/create-account/CreateAccount.tsx")
);

const ViewCertificateDetails = lazy(
  () => import("@/pages/certificatedetails/ViewCertificateDetails.tsx")
);
const LicencingAdminCouncilsConfiguration = lazy(
  () => import("@/pages/licencing-admin/control-panel/councils-configuration")
);
const LicencingAdminStudyLevelConfiguration = lazy(
  () =>
    import("@/pages/licencing-admin/control-panel/study-level-configuration")
);
const LicencingAdminFinesConfiguration = lazy(
  () => import("@/pages/licencing-admin/control-panel/fines-configuration")
);

const CertificateVerification = lazy(
  () => import("./pages/verify-certificate")
);

const NotFound = lazy(() => import("./pages/NotFound"));

export const allRoutesWithLayout: {
  path: string;
  routes: SideBarRouteOption[];
  children: { path: string; element: JSX.Element }[];
}[] = [
  {
    path: "/licencing-admin",
    routes: LicencingAdminRoutes,
    children: [
      {
        path: "",
        element: <AnalyticsDashboard />,
      },
      {
        path: "profile",
        element: <ProfileView />,
      },
      {
        path: "post-secondary-education/exitaward",
        element: <LicencingAdminPostSecondaryEducationExitAward />,
      },
      {
        path: "post-secondary-education/professions-category",
        element: <LicencingAdminPostSecondaryEducation />,
      },
      {
        path: "post-secondary-education/university-configuration",
        element: <LicencingAdminUniversityConfiguration />,
      },
      {
        path: "post-secondary-education/academic-degree-program",
        element: <AcademicDegreeProgram />,
      },
      {
        path: "control-panel/services-configuration",
        element: <LicencingAdminServicesConfiguration />,
      },
      {
        path: "control-panel/councils-configuration",
        element: <LicencingAdminCouncilsConfiguration />,
      },
      {
        path: "secondary-education/document-title",
        element: <LicencingAdminSecondaryEducationDocumentTitle />,
      },
      {
        path: "post-secondary-education/professions-credential",
        element: <LicencingAdminSecondaryEducationProfessionsCredential />,
      },
      {
        path: "control-panel/bank-configuration",
        element: <LicencingAdminControlPanelBankConfiguration />,
      },
      {
        path: "control-panel/licence-fee-configuration",
        element: <LicencingAdminControlPanelLicenceFeeConfiguration />,
      },
      {
        path: "control-panel/study-level-configuration",
        element: <LicencingAdminStudyLevelConfiguration />,
      },
      {
        path: "control-panel/fines-configuration",
        element: <LicencingAdminFinesConfiguration />,
      },
      {
        path: "system-settings/users",
        element: <LicencingAdminSystemUsers />,
      },
      {
        path: "system-settings/roles",
        element: <LicencingAdminRoles />,
      },
      {
        path: "system-settings/approval-structure",
        element: <ApprovalStructure />,
      },
    ],
  },
  {
    path: "/end-user-account",
    routes: licencingEndUserRoutes,
    children: [
      {
        path: "",
        element: <LicencingEndUserAnalytics />,
      },
      {
        path: "profile",
        element: <ProfileView />,
      },
      {
        path: "Certificates",
        element: <UserCertificates />,
      },
      {
        path: "licensedownload",
        element: <UserCertificatesDownload />,
      },
      {
        path: "user-profile-settings",
        element: <UserProfileSettings />,
      },
      {
        path: "system-settings/users",
        element: <LicencingAdminSystemUsers />,
      },
      {
        path: "user-profile-record",
        element: <UserProfileRecords />,
      },
    ],
  },
  {
    path: "/approvalofficesaccount",
    routes: licencingApprovalOfficesRoutes,
    children: [
      {
        path: "",
        element: <LicencingApprovalOfficesAccountStatistics />,
      },
      {
        path: "analytics",
        element: <ApprovalOfficerAnalyticsDashboard />,
      },
      {
        path: "applications/allapplications",
        element: <LicencingApprovalOfficesAccountAllApplications />,
      },

      {
        path: "applications/onholdapplications",
        element: <LicencingApprovalOfficesAccountOnHoldApplications />,
      },
      {
        path: "applications/approved-applications",
        element: <LicencingApprovalOfficesAccountApprovedApplications />,
      },
      {
        path: "applications/applications-modification",
        element: <LicencingApprovalOfficesAccountApplicationsModifications />,
      },
      {
        path: "rejectedreports/indexing",
        element: <LicencingApprovalOfficesAccountIndexingRejected />,
      },
      {
        path: "rejectedreports/licenserenew",
        element: <LicencingApprovalOfficesAccountLecenseRenew />,
      },
      {
        path: "registrationfeeapplications/:userApplicationId",
        element: <RegistrationFeeApplications />,
      },
      {
        path: "modifications/ManualAssignRegNo",
        element: <LicencingApprovalOfficesAccountManualAssignRegNo />,
      },
      {
        path: "modifications/ManualAssignIndexing",
        element: <LicencingApprovalOfficesAccountManualAssignIndexing />,
      },
      {
        path: "modifications/ManualAssignExistingIndex",
        element: <LicencingApprovalOfficesAccountManualAssignExistingIndex />,
      },
      {
        path: "modifications/ManualAssignExistingRegistration",
        element: (
          <LicencingApprovalOfficesAccountManualAssignExistingRegistration />
        ),
      },
    ],
  },
];

const AuthRoute = () => {
  const token = Cookies.get("token");
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const { user } = useAuth();

  useEffect(() => {
    if (!pathname.includes("/auth/") && token) {
      // console.log(pathname);
      const allowedRoutes = user?.roles
        ?.map((role) => ROLE_TO_ROUTES[role.roleName])
        .flat()
        .concat(ROLE_TO_ROUTES.common);
      if (!allowedRoutes?.some((route) => pathname.startsWith(route))) {
        //console.log(allowedRoutes);
        navigate("/");
        // console.log('not allowed', pathname, user?.roles.map((el) => el.roleName), allowedRoutes);
      }
    }
  }, [navigate, pathname, token, user?.roles]);

  if (token) {
    return <Outlet />;
  }
  return <Navigate to="/auth/login" />;
};

export default function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <SuspenseWithLoader>
            <IndexPage />
          </SuspenseWithLoader>
        }
      />

      <Route
        path="/homepage"
        element={
          <SuspenseWithLoader>
            <HomePage />
          </SuspenseWithLoader>
        }
      />
      <Route
        path="/verify-certificate"
        element={
          <SuspenseWithLoader>
            <CertificateVerification />
          </SuspenseWithLoader>
        }
      />

      <Route
        path="/auth/login"
        element={
          <SuspenseWithLoader>
            <LoginPage />
          </SuspenseWithLoader>
        }
      />
      <Route
        path="/auth/forgot-password"
        element={
          <SuspenseWithLoader>
            <ResetPassword />
          </SuspenseWithLoader>
        }
      />
      <Route
        path="/auth/forgot-password-bylicense"
        element={
          <SuspenseWithLoader>
            <ResetPasswordByLicense />
          </SuspenseWithLoader>
        }
      />
      <Route
        path="/auth/reset-password/:code"
        element={
          <SuspenseWithLoader>
            <VerifyResetCode />
          </SuspenseWithLoader>
        }
      />
      <Route
        path="/auth/verify-email"
        element={
          <SuspenseWithLoader>
            <VerifyEmail />
          </SuspenseWithLoader>
        }
      />
      <Route
        path="/auth/accept-invitation"
        element={
          <SuspenseWithLoader>
            <AcceptInvitation />
          </SuspenseWithLoader>
        }
      />

      <Route
        path="/create-account/CreateAccount"
        element={
          <SuspenseWithLoader>
            <CreateAccount />
          </SuspenseWithLoader>
        }
      />
      <Route
        path="/certificate-details/ViewCertificateDetails"
        element={
          <SuspenseWithLoader>
            <ViewCertificateDetails />
          </SuspenseWithLoader>
        }
      />
      {/* Protected Auth Routes */}
      <Route
        element={
          <SuspenseWithLoader>
            <AuthRoute />
          </SuspenseWithLoader>
        }
      >
        {allRoutesWithLayout.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={
              <SuspenseWithLoader>
                <MainLayout routes={route.routes} />
              </SuspenseWithLoader>
            }
          >
            {route.children.map((child) => (
              <Route
                key={child.path}
                path={child.path}
                element={
                  <SuspenseWithLoader>{child.element}</SuspenseWithLoader>
                }
              />
            ))}
          </Route>
        ))}
        <Route
          path="/profile"
          element={
            <SuspenseWithLoader>
              <ProfileView />
            </SuspenseWithLoader>
          }
        />
        <Route
          path="/user-profile-settings"
          element={
            <SuspenseWithLoader>
              <UserProfileSettings />
            </SuspenseWithLoader>
          }
        />
        <Route
          path="/user-profile-record"
          element={
            <SuspenseWithLoader>
              <UserProfileRecords />
            </SuspenseWithLoader>
          }
        />
      </Route>
      <Route
        path="*"
        element={
          <SuspenseWithLoader>
            <NotFound />
          </SuspenseWithLoader>
        }
      />
    </Routes>
  );
}
